import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { FORTRESS_CARDS_CONFIG, type FortressCard } from "@fortress/shared";
import { Glyph, SHAPE_LABEL } from "./fortressPattern";
import { FortressCardFace, REGULAR_ACCENT, WIN_ACCENT } from "./FortressCardFace";

// All cards, normalised to one list (each already carries its `kind`).
const ALL_CARDS: FortressCard[] = [
  ...(FORTRESS_CARDS_CONFIG.winCards as FortressCard[]),
  ...(FORTRESS_CARDS_CONFIG.regularCards as FortressCard[]),
];

function Section({
  title,
  accent,
  cards,
}: {
  title: string;
  accent: string;
  cards: FortressCard[];
}) {
  if (cards.length === 0) return null;
  return (
    <section style={{ marginBottom: 28 }}>
      <h2
        style={{
          fontSize: 18,
          margin: "0 0 12px",
          paddingBottom: 6,
          borderBottom: `2px solid ${accent}`,
        }}
      >
        {title}{" "}
        <span style={{ color: "#888", fontWeight: 400, fontSize: 14 }}>
          ({cards.length})
        </span>
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
        }}
      >
        {cards.map((c) => (
          <div key={c.id} className="glossary-card">
            <FortressCardFace card={c} />
          </div>
        ))}
      </div>
    </section>
  );
}

/** The full Fortress-card catalogue. Rendered as a standalone page at
 * `/library/fortresses`, and — when `onClose` is supplied — inside the in-game
 * full-screen modal, where the nav links are swapped for a Close button. */
export function FortressLibrary({ onClose }: { onClose?: () => void } = {}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  // In modal mode, Escape closes it (mirrors the detail popovers).
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { wins, regulars } = useMemo(() => {
    const match = (c: FortressCard) =>
      q === "" ||
      c.name.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q);
    return {
      wins: ALL_CARDS.filter((c) => c.kind === "win" && match(c)),
      regulars: ALL_CARDS.filter((c) => c.kind === "REGULAR" && match(c)),
    };
  }, [q]);

  return (
    <div
      style={{
        // Same self-scroll trick as the Tile Library: #root is locked to
        // height:100% with body overflow hidden for the game canvas, so this
        // page must be its own scroll container.
        height: "100vh",
        overflowY: "auto",
        background: "#f2ecda",
        color: "#2b2620",
        padding: "24px 28px",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24 }}>Fortress Library</h1>
        <span style={{ color: "#777" }}>{ALL_CARDS.length} cards</span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or text…"
          style={searchStyle}
        />
        {onClose ? (
          <button type="button" onClick={onClose} style={closeBtnStyle}>
            ✕ Close
          </button>
        ) : (
          <>
            <a
              href="/library/fortresses/edit"
              style={{ marginLeft: "auto", color: "#3f7fd0" }}
            >
              edit patterns →
            </a>
            <a href="/" style={{ color: "#3f7fd0" }}>
              ← back to game
            </a>
          </>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 16,
          marginBottom: 6,
          fontSize: 13,
          color: "#555",
        }}
      >
        {(["scout", "barracks", "mine", "wild"] as const).map((k) => (
          <span
            key={k}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <svg width={20} height={18} viewBox="0 0 20 18">
              <Glyph kind={k} cx={10} cy={9} />
            </svg>
            {SHAPE_LABEL[k]}
          </span>
        ))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width={20} height={18} viewBox="0 0 20 18">
            <Glyph kind="marker" cx={10} cy={9} />
          </svg>
          Marker
        </span>
      </div>
      <p style={{ color: "#777", marginTop: 0, fontSize: 12.5, maxWidth: 760 }}>
        Each card shows both build orientations (a pattern and its mirror), like
        the printed card — the matcher tries every rotation/mirror, so only one is
        stored. Patterns are hand-read from the art
        {onClose ? (
          "."
        ) : (
          <>
            ; refine them on the{" "}
            <a href="/library/fortresses/edit" style={{ color: "#3f7fd0" }}>
              pattern editor
            </a>
            .
          </>
        )}
      </p>

      <Section title="Win Fortresses" accent={WIN_ACCENT} cards={wins} />
      <Section title="Regular Fortresses" accent={REGULAR_ACCENT} cards={regulars} />
      {wins.length === 0 && regulars.length === 0 && (
        <p style={{ color: "#777" }}>No cards match “{query}”.</p>
      )}
    </div>
  );
}

const closeBtnStyle: CSSProperties = {
  marginLeft: "auto",
  padding: "7px 14px",
  border: "1px solid #ccc3ac",
  borderRadius: 8,
  background: "#fff",
  color: "#2b2620",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const searchStyle: CSSProperties = {
  padding: "6px 10px",
  border: "1px solid #ccc3ac",
  borderRadius: 8,
  background: "#fff",
  color: "#2b2620",
  fontSize: 13,
  minWidth: 220,
};
